import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { VpPurchaseOrder } from '../models/vp-purchase-order.model';
import {
  PURCHASE_ORDER_SEARCH_TOP,
  toVpPurchaseOrder,
  VpPurchaseOrderRecord,
} from '../utils/purchase-order-search.utils';
import { vendorGroupForCompany } from '../utils/vendor-search.utils';

/** WI 34963: Viewpoint purchase order search for the `typeahead-purchase-order` dynamic control. */
@Injectable({
  providedIn: 'root',
})
export class SearchVpPurchaseOrderService {
  private readonly http: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /**
   * POs of company `vpCompanyId` matching `query`, newest first. No usable company returns an
   * empty list, never another company's POs.
   */
  search(
    query: string,
    vpCompanyId: number | null | undefined
  ): Observable<Array<VpPurchaseOrder>> {
    // A usable Viewpoint company number is the same 1..255 tinyint check the vendor group uses.
    const company = vendorGroupForCompany(vpCompanyId);
    if (company == null) {
      return of([]);
    }
    let params = new HttpParams()
      .set('vpCompanyId', company)
      .set('top', PURCHASE_ORDER_SEARCH_TOP);
    const term = `${query ?? ''}`.trim();
    if (term) {
      params = params.set('search', term);
    }
    return this.http
      .get<Array<VpPurchaseOrderRecord>>(
        '/api/vpservice/purchaseOrders/search',
        { params }
      )
      .pipe(map((records) => (records ?? []).map(toVpPurchaseOrder)));
  }

  /**
   * `search`, once `company$` first emits. A form's reopen prefill can search before the user's
   * profile loads; waiting keeps that search from failing closed and dropping the saved PO.
   */
  searchForCompany(
    query: string,
    company$: Observable<number | null | undefined>
  ): Observable<Array<VpPurchaseOrder>> {
    return company$.pipe(
      take(1),
      switchMap((company) => this.search(query, company))
    );
  }
}
